<template>
    <inline-tab-panel :name="props.name">
        <card-normal without-padding>
            <list-container>
                <template #header>
                    <list-cell-header>
                        <template #title>
                            Naam
                        </template>
                    </list-cell-header>
                    <list-cell-header>
                        <template #title>
                            Aangemaakt door
                        </template>
                    </list-cell-header>
                    <list-cell-header>
                        <template #title>
                            Aanmaakdatum
                        </template>
                    </list-cell-header>
                    <list-cell-header>
                        <template #title>
                            Soort
                        </template>
                    </list-cell-header>
                    <list-cell-header>
                        <template #title>
                            Status
                        </template>
                    </list-cell-header>
                    <list-cell-header />
                </template>

                <template #content>
                    <list-row v-for="template in props.templates" :key="template.id.toString()">
                        <list-cell-text>
                            <template #content>
                                {{ template.name }}
                            </template>
                        </list-cell-text>
                        <list-cell-text>
                            <template #content>
                                <span style="display:flex; align-items: center;">
                                    <user-avatar
                                        :placeholder="template.createdBy.name.abbreviation"
                                        size="md"
                                        :url="template.createdBy.avatar ? resizedToSizeUploadUrl(template.createdBy.avatar, 'md') : ''"
                                        :alt="template.createdBy.name.fullName"
                                    />
                                    <span>&nbsp;{{ template.createdBy.name }}</span>
                                </span>
                            </template>
                        </list-cell-text>
                        <list-cell-text>
                            <template #content>
                                {{ formatDate(template.createdAt) }}
                            </template>
                        </list-cell-text>
                        <list-cell-text>
                            <template #content>
                                <template v-if="template instanceof DocumentTemplate">
                                    {{ template.layout.type.label }}
                                </template>
                                <template v-else>
                                    Woningbrochure
                                </template>
                            </template>
                        </list-cell-text>
                        <list-cell-text>
                            <template #content>
                                <span v-if="template.active">Actief</span>
                                <span v-else>Inactief</span>
                            </template>
                        </list-cell-text>
                        <list-cell>
                            <flex-container
                                align-content="center"
                                justify-content="center"
                                style="height: 100%"
                            >
                                <button-outline-icon
                                    :icon="IconDelete"
                                    :color="Color.red500"
                                    :disabled="!canDelete(template)"
                                    @click="() => onDeleteClick(template)"
                                />
                                <button-outline-icon
                                    :icon="IconEdit"
                                    :color="Color.green500"
                                    :disabled="!canEdit(template)"
                                    @click="() => onEditClick(template)"
                                />
                            </flex-container>
                        </list-cell>
                    </list-row>
                </template>
            </list-container>

            <list-message-empty v-if="props.templates.length === 0">
                <slot name="empty-message">
                    Er zijn geen templates gevonden
                </slot>
            </list-message-empty>
        </card-normal>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonOutlineIcon,
        CardNormal,
        Color,
        FlexContainer,
        ListContainer,
        ListCell,
        ListCellHeader,
        ListCellText,
        ListMessageEmpty,
        ListRow,
        InlineTabPanel,
        UserAvatar,
    } from '@keyvue/one-ui';

    import {
        Template as BrochureTemplate,
        DocumentTemplate,
    } from '@keyvue/one-api-client';

    import IconEdit from '@keyvue/one-material-icons/icons/edit.svg?component';
    import IconDelete from '@keyvue/one-material-icons/icons/delete.svg?component';

    import { formatDate } from '@/utils/date';
    import { resizedToSizeUploadUrl } from '@/utils/resize';

    interface Props {
        name: string,
        templates: Array<BrochureTemplate | DocumentTemplate>,
    }

    const props = defineProps<Props>();

    const emit = defineEmits(['edit-template', 'delete-template']);

    const canEdit = (template: BrochureTemplate | DocumentTemplate) => {
        if (template instanceof DocumentTemplate) {
            return template.layout.editable || template.layout.configurable;
        }

        return true;
    };

    const onEditClick = (template: BrochureTemplate | DocumentTemplate) => {
        emit('edit-template', template);
    };

    const canDelete = (template: BrochureTemplate | DocumentTemplate) => {
        if (template instanceof DocumentTemplate) {
            return template.layout.editable;
        }

        return true;
    };

    const onDeleteClick = (template: BrochureTemplate | DocumentTemplate) => {
        emit('delete-template', template);
    };
</script>

<style scoped lang="scss">
.list-row {
    button {
        opacity: 0;
    }

    &:hover {
        button {
            opacity: 1;
        }
    }
}
</style>
